import type { EnemyType, PickupType } from '../ecs/entity'

/**
 * THE level schema — FROZEN. One JSON structure used by both authored levels
 * and the procedural generator; nothing downstream may care where a level
 * came from (hard rule). Coordinates are tile units.
 */
export interface LevelData {
  id: string
  name: string
  /** 1-based campaign floor number (procgen stamps this at runtime) */
  floor: number
  act: number
  /** difficulty band, 0-based; scales enemy/generator budgets */
  band: number
  isBossGate: boolean
  /** tile rows, single-char codes, all rows equal width */
  tiles: string[]
  generators: GeneratorPlacement[]
  enemies: EnemyPlacement[]
  pickups: PickupPlacement[]
  /** plural on purpose — co-op ready */
  playerSpawns: { x: number; y: number }[]
  /** how the exit unlocks; key-gates are expressed with locked-door tiles */
  exit: { type: 'reach' | 'generators' | 'boss' }
  boss?: { id: string; x: number; y: number }
  /** Death-type (Auditor) spawn schedule, seconds after level start */
  auditorSpawns?: { delay: number }[]
}

export interface GeneratorPlacement {
  type: Exclude<EnemyType, 'auditor'>
  x: number
  y: number
  /** total spawns before exhaustion; omit for band default */
  budget?: number
  /** seconds between spawns; omit for band default */
  interval?: number
}

export interface EnemyPlacement {
  type: Exclude<EnemyType, 'auditor'>
  x: number
  y: number
}

export interface PickupPlacement {
  type: PickupType
  x: number
  y: number
}

/**
 * Tile codes used in LevelData.tiles (frozen with the schema).
 * DECISION (spec-level, operator-ratified 2026-07-09): the legend gains two
 * puzzle codes — 'R' relay plate and 'G' shutter. Latching every relay on a
 * floor retracts every shutter on it. Additive only; the procedural generator
 * does not emit them, and floors without them behave exactly as before.
 */
export const TILE_CODES = {
  wall: '#',
  floor: '.',
  void: ' ',
  door: 'D',
  lockedDoor: 'L',
  exit: 'E',
  destructible: 'X',
  trap: 'T',
  teleporters: ['1', '2', '3', '4'],
  relay: 'R',
  shutter: 'G'
} as const

/**
 * Entity glyphs allowed inside authored ASCII maps; compiled out to placements.
 * DECISION: the archive fragment ('a') enters through the pickup type table —
 * PickupPlacement's shape is unchanged, so the frozen level JSON schema is
 * untouched; both authored maps and procgen emit it as an ordinary pickup.
 */
const GLYPHS: Record<string, { kind: 'spawn' } | { kind: 'gen'; type: EnemyPlacement['type'] } | { kind: 'enemy'; type: EnemyPlacement['type'] } | { kind: 'pickup'; type: PickupType } | { kind: 'boss' }> = {
  '@': { kind: 'spawn' },
  g: { kind: 'gen', type: 'husk' },
  r: { kind: 'gen', type: 'sentry' },
  w: { kind: 'gen', type: 'static' },
  m: { kind: 'gen', type: 'foreman' },
  h: { kind: 'enemy', type: 'husk' },
  n: { kind: 'enemy', type: 'sentry' },
  o: { kind: 'enemy', type: 'static' },
  u: { kind: 'enemy', type: 'foreman' },
  f: { kind: 'pickup', type: 'food' },
  k: { kind: 'pickup', type: 'key' },
  p: { kind: 'pickup', type: 'magic' },
  t: { kind: 'pickup', type: 'treasure' },
  a: { kind: 'pickup', type: 'archive' },
  B: { kind: 'boss' }
}

export interface CompileMeta {
  id: string
  name: string
  floor: number
  act: number
  band: number
  isBossGate?: boolean
  exit?: LevelData['exit']['type']
  bossId?: string
  auditorDelays?: number[]
  /** overrides for generator placements by index (order of appearance, row-major) */
  genOverrides?: { budget?: number; interval?: number }[]
}

/**
 * Compile an ASCII map (tiles + entity glyphs) into the frozen LevelData
 * schema. Authored levels use this for readability; the OUTPUT is the same
 * JSON structure the procedural generator emits.
 */
const VALID_TILE_CHARS = new Set([' ', '.', '#', 'D', 'L', 'E', 'X', 'T', '1', '2', '3', '4', 'R', 'G'])

export function compileLevel(meta: CompileMeta, rawRows: string[]): LevelData {
  // normalize: pad short rows with wall (always safe at edges); authoring aid
  const width = Math.max(...rawRows.map((r) => r.length))
  const rows = rawRows.map((r) => r.padEnd(width, '#'))
  const tiles: string[] = []
  const generators: GeneratorPlacement[] = []
  const enemies: EnemyPlacement[] = []
  const pickups: PickupPlacement[] = []
  const playerSpawns: { x: number; y: number }[] = []
  let boss: LevelData['boss']

  rows.forEach((row, y) => {
    let out = ''
    for (let x = 0; x < row.length; x++) {
      const ch = row[x]!
      const glyph = GLYPHS[ch]
      if (!glyph) {
        if (!VALID_TILE_CHARS.has(ch)) throw new Error(`level ${meta.id}: unknown char '${ch}' at ${x},${y}`)
        out += ch
        continue
      }
      out += '.'
      switch (glyph.kind) {
        case 'spawn':
          playerSpawns.push({ x, y })
          break
        case 'gen': {
          const idx = generators.length
          const o = meta.genOverrides?.[idx]
          generators.push({ type: glyph.type, x, y, ...(o?.budget !== undefined ? { budget: o.budget } : {}), ...(o?.interval !== undefined ? { interval: o.interval } : {}) })
          break
        }
        case 'enemy':
          enemies.push({ type: glyph.type, x, y })
          break
        case 'pickup':
          pickups.push({ type: glyph.type, x, y })
          break
        case 'boss':
          boss = { id: meta.bossId ?? 'warden', x, y }
          break
      }
    }
    tiles.push(out)
  })

  const level: LevelData = {
    id: meta.id,
    name: meta.name,
    floor: meta.floor,
    act: meta.act,
    band: meta.band,
    isBossGate: meta.isBossGate ?? false,
    tiles,
    generators,
    enemies,
    pickups,
    playerSpawns,
    exit: { type: meta.exit ?? 'reach' },
    ...(boss ? { boss } : {}),
    ...(meta.auditorDelays ? { auditorSpawns: meta.auditorDelays.map((delay) => ({ delay })) } : {})
  }
  validateLevel(level)
  return level
}

export function validateLevel(l: LevelData): void {
  const w = l.tiles[0]?.length ?? 0
  if (w === 0 || l.tiles.length === 0) throw new Error(`level ${l.id}: empty grid`)
  for (const [y, row] of l.tiles.entries()) {
    if (row.length !== w) throw new Error(`level ${l.id}: ragged row ${y}`)
  }
  if (l.playerSpawns.length === 0) throw new Error(`level ${l.id}: no player spawn`)
  const hasExit = l.tiles.some((r) => r.includes(TILE_CODES.exit))
  if (!hasExit && !l.isBossGate) throw new Error(`level ${l.id}: no exit tile`)
  if (l.isBossGate && !l.boss) throw new Error(`level ${l.id}: boss gate without boss`)
  for (const t of ['1', '2', '3', '4']) {
    const count = l.tiles.join('').split(t).length - 1
    if (count !== 0 && count !== 2) throw new Error(`level ${l.id}: teleporter '${t}' needs exactly 2 pads, has ${count}`)
  }
  // relays and shutters only make sense together
  const flat = l.tiles.join('')
  const relays = flat.split('R').length - 1
  const shutters = flat.split('G').length - 1
  if ((relays === 0) !== (shutters === 0)) {
    throw new Error(`level ${l.id}: relays (${relays}) and shutters (${shutters}) must both be present or both absent`)
  }
}
